import React from 'react';
import LoadingSequence from 'assets/Loading/LoadingSequence';
import WidgetDiagnostics from './WidgetDiagnostics';
import { useWidgetFetch } from 'hooks/useWidgetFetch';
import { apiPath } from 'apiurl';

const STATS_URL = `${apiPath}/api/processedrun/stats?timeRange=all`;
const TTL = 10 * 60 * 1000;
const SWR = 20 * 60 * 1000;

const getCurrentMonth = () => {
  const names = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  return names[new Date().getMonth()];
};

const getChangeArrow = (change) => {
  if (change === null || change === undefined) return 'bi bi-dash-circle text-secondary';
  return change > 0 ? 'bi bi-arrow-up-circle-fill' : change < 0 ? 'bi bi-arrow-down-circle-fill' : 'bi bi-dash-circle';
};

const formatChangeText = (change) => {
  if (change === null || change === undefined) return 'No change';
  return change > 0 ? `+${change.toFixed(1)}%` : change < 0 ? `${change.toFixed(1)}%` : 'No change';
};

function StatsCardGroup() {
  const { data, loading, timing } = useWidgetFetch(STATS_URL, { ttl: TTL, swr: SWR, widgetId: 'Stats (KPI)' });

  const totalMonthlyRuns   = data?.totalMonthlyRuns   ?? 0;
  const last24HoursRuns    = data?.last24HoursRuns    ?? null;
  const totalYTDRuns       = data?.totalYTDRuns       ?? 0;
  const totalFileSizeGB    = data?.totalFileSizeGB    ?? 0;
  const totalRuns          = data?.totalRuns          ?? 0;
  const monthlyRunChange   = data?.monthlyRunChange   ?? null;

  const changeClass = monthlyRunChange === null ? 'neutral'
    : monthlyRunChange > 0 ? 'up' : monthlyRunChange < 0 ? 'down' : 'neutral';

  return (
    <>
      {/* Monthly Genomic Sequences */}
      <div className="kpi-card kpi-card--blue kpi-card--diagnostics-outside" style={{ position: 'relative' }}>
        <WidgetDiagnostics timing={timing} url={STATS_URL} panelPlacement="above" />
        {loading ? <LoadingSequence message="" size="small" /> : (
          <>
            <p className="kpi-label">{getCurrentMonth()} Genomic Sequences</p>
            <div className="kpi-value">{Number(totalMonthlyRuns).toLocaleString()}</div>
            {monthlyRunChange !== null && (
              <div className={`kpi-change ${changeClass}`}>
                <i className={getChangeArrow(monthlyRunChange)}></i>
                <span>{formatChangeText(monthlyRunChange)}</span>
                <span className="kpi-vs">vs Last Month</span>
              </div>
            )}
            {last24HoursRuns !== null && (
              <div className="kpi-last24">
                <span className="kpi-last24-count">{Number(last24HoursRuns).toLocaleString()}</span>
                <span className="kpi-last24-label">last 24 hrs</span>
              </div>
            )}
          </>
        )}
      </div>

      {/* YTD Sequences */}
      <div className="kpi-card kpi-card--teal kpi-card--diagnostics-outside" style={{ position: 'relative' }}>
        <WidgetDiagnostics timing={timing} url={STATS_URL} panelPlacement="above" />
        {loading ? <LoadingSequence message="" size="small" /> : (
          <>
            <p className="kpi-label">YTD Sequences</p>
            <div className="kpi-value">{Number(totalYTDRuns).toLocaleString()}</div>
          </>
        )}
      </div>

      {/* Data Archive */}
      <div className="kpi-card kpi-card--orange kpi-card--diagnostics-outside" style={{ position: 'relative' }}>
        <WidgetDiagnostics timing={timing} url={STATS_URL} panelPlacement="above" />
        {loading ? <LoadingSequence message="" size="small" /> : (
          <>
            <p className="kpi-label">Data Archive (GB)</p>
            <div className="kpi-value">{totalFileSizeGB}</div>
          </>
        )}
      </div>

      {/* Surveillance Archive */}
      <div className="kpi-card kpi-card--purple kpi-card--diagnostics-outside" style={{ position: 'relative' }}>
        <WidgetDiagnostics timing={timing} url={STATS_URL} panelPlacement="above" />
        {loading ? <LoadingSequence message="" size="small" /> : (
          <>
            <p className="kpi-label">Surveillance Archive</p>
            <div className="kpi-value">{Number(totalRuns).toLocaleString()}</div>
          </>
        )}
      </div>
    </>
  );
}

export default StatsCardGroup;
